import { useState, ChangeEvent } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import DestinationSearchInput from "./DestinationSearchInput";

type Props = {
  onClose: () => void;
  onCreated?: () => void;
};

const TOTAL_STEPS = 5;

export default function CreateTripModal({ onClose, onCreated }: Props) {
  const { user } = useAuth();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [destination, setDestination] = useState("");
  const [destinationCoords, setDestinationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState("");
  const [joinSelf, setJoinSelf] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const step1Valid = name.trim() !== "";
  const step2Valid =
    startDate !== "" && endDate !== "" && new Date(startDate) <= new Date(endDate);

  const handleSubmit = async () => {
    setError("");
    setUploading(true);

    if (!user) return;

    let coverImageUrl = "";

    if (imageFile) {
      const fileExt = imageFile.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("trip-images")
        .upload(filePath, imageFile, {
          contentType: imageFile.type,
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        setError("Fehler beim Hochladen des Bildes");
        setUploading(false);
        return;
      }

      coverImageUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/trip-images/${filePath}`;
    }

    const { data: insertedTrip, error: insertError } = await supabase
      .from("trips")
      .insert({
        creator_id: user.id,
        name: name.trim(),
        description: description.trim(),
        destination: destination.trim(),
        start_date: startDate,
        end_date: endDate,
        is_public: isPublic,
        requires_approval: requiresApproval,
        max_participants: maxParticipants ? parseInt(maxParticipants) : null,
        cover_image_url: coverImageUrl,
        status: "draft",
        destination_lat: destinationCoords?.lat ?? null,
        destination_lng: destinationCoords?.lng ?? null,
      })
      .select("id")
      .single();

    if (insertError) {
      setError("Fehler beim Speichern der Reise");
      setUploading(false);
      return;
    }

    if (joinSelf && insertedTrip?.id) {
      await supabase.from("trip_participants").insert({
        trip_id: insertedTrip.id,
        user_id: user.id,
      });
    }

    onCreated?.();
    onClose();
    setUploading(false);
  };

  const inputClass =
    "w-full bg-[#0d1117] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 placeholder:text-slate-600";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[#2a3347]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500">
              Schritt {step} von {TOTAL_STEPS}
            </span>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 text-xl transition-colors"
              aria-label="Schließen"
            >
              &times;
            </button>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  i + 1 === step
                    ? "bg-sky-400"
                    : i + 1 < step
                    ? "bg-sky-400/40"
                    : "bg-[#2a3347]"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Name & Ziel */}
          {step === 1 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Name & Ziel</h2>

              <label className="block mb-1 text-sm text-slate-400 font-medium">Reisename *</label>
              <input
                type="text"
                placeholder="z. B. Sommerreise 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className={`${inputClass} mb-4`}
              />

              <label className="block mb-1 text-sm text-slate-400 font-medium">Beschreibung</label>
              <textarea
                placeholder="Kurze Beschreibung der Reise…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className={`${inputClass} resize-none mb-4`}
              />

              <label className="block mb-1 text-sm text-slate-400 font-medium">Reiseziel</label>
              <DestinationSearchInput
                value={destination}
                onChange={(val) => setDestination(val)}
                onCoordinates={(lat, lng) => setDestinationCoords({ lat, lng })}
              />

              {destinationCoords && (
                <div className="w-full h-44 mt-3 rounded-xl overflow-hidden border border-[#2a3347]">
                  <iframe
                    width="100%"
                    height="100%"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed/v1/view?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&center=${destinationCoords.lat},${destinationCoords.lng}&zoom=12`}
                    title="Zielkarte"
                  />
                </div>
              )}
            </>
          )}

          {/* Step 2: Datum */}
          {step === 2 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Reisedaten</h2>

              <label className="block mb-1 text-sm text-slate-400 font-medium">Startdatum *</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`${inputClass} mb-4`}
              />

              <label className="block mb-1 text-sm text-slate-400 font-medium">Enddatum *</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`${inputClass} mb-2`}
              />

              {startDate && endDate && new Date(startDate) > new Date(endDate) && (
                <p className="text-red-400 text-xs mt-1">Enddatum muss nach dem Startdatum liegen.</p>
              )}
            </>
          )}

          {/* Step 3: Einstellungen */}
          {step === 3 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Einstellungen</h2>

              <label className="block mb-1 text-sm text-slate-400 font-medium">Max. Teilnehmer</label>
              <input
                type="number"
                placeholder="Unbegrenzt"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(e.target.value)}
                min="1"
                className={`${inputClass} mb-5`}
              />

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={joinSelf}
                    onChange={(e) => setJoinSelf(e.target.checked)}
                    className="w-4 h-4 accent-sky-500"
                  />
                  <span className="text-sm text-slate-300">Ich nehme selbst teil</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    className="w-4 h-4 accent-sky-500"
                  />
                  <div>
                    <span className="text-sm text-slate-300">Öffentlich sichtbar</span>
                    <p className="text-xs text-slate-500 mt-0.5">Andere können deine Reise in der Übersicht sehen</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresApproval}
                    onChange={(e) => setRequiresApproval(e.target.checked)}
                    className="w-4 h-4 accent-sky-500"
                  />
                  <div>
                    <span className="text-sm text-slate-300">Genehmigung erforderlich</span>
                    <p className="text-xs text-slate-500 mt-0.5">Neue Teilnehmer müssen vom Orga-Team genehmigt werden</p>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Step 4: Bild */}
          {step === 4 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-2">Titelbild</h2>
              <p className="text-sm text-slate-500 mb-5">Optional – wird als Hero-Bild in der Reise-Übersicht verwendet.</p>

              {imagePreview ? (
                <div className="relative mb-4">
                  <img
                    src={imagePreview}
                    alt="Vorschau"
                    className="w-full h-48 object-cover rounded-xl border border-[#2a3347]"
                  />
                  <button
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-lg hover:bg-black/80 transition-colors"
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[#2a3347] rounded-xl cursor-pointer hover:border-sky-500/40 transition-colors mb-4">
                  <span className="text-3xl mb-2">🖼</span>
                  <span className="text-sm text-slate-400">Bild auswählen</span>
                  <span className="text-xs text-slate-600 mt-1">JPG, PNG, WEBP</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
            </>
          )}

          {/* Step 5: Bestätigung */}
          {step === 5 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Alles bereit?</h2>

              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Titelbild"
                  className="w-full h-32 object-cover rounded-xl border border-[#2a3347] mb-4"
                />
              )}

              <div className="bg-[#1e2639] border border-[#2a3347] rounded-xl p-4 space-y-2.5 mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Reisename</span>
                  <span className="text-slate-100 font-medium truncate ml-4 text-right">{name}</span>
                </div>
                {destination && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Ziel</span>
                    <span className="text-slate-300 truncate ml-4 text-right">{destination}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Zeitraum</span>
                  <span className="text-slate-300">
                    {new Date(startDate).toLocaleDateString("de-CH")} – {new Date(endDate).toLocaleDateString("de-CH")}
                  </span>
                </div>
                {maxParticipants && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Max. Teilnehmer</span>
                    <span className="text-slate-300">{maxParticipants}</span>
                  </div>
                )}
                <div className="flex gap-2 flex-wrap pt-1">
                  {joinSelf && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20">Ich nehme teil</span>}
                  {isPublic && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1e2639] text-slate-400 border border-[#2a3347]">Öffentlich</span>}
                  {requiresApproval && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Genehmigung nötig</span>}
                </div>
              </div>

              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2a3347] flex gap-2">
          {step > 1 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex-1 py-2.5 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
            >
              ← Zurück
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
            >
              Abbrechen
            </button>
          )}

          {step < TOTAL_STEPS ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={
                (step === 1 && !step1Valid) ||
                (step === 2 && !step2Valid)
              }
              className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Weiter →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {uploading ? "Wird erstellt…" : "✓ Reise erstellen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
